import React from 'react';
import { Check, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { packsData } from '../../data/packs';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getWhatsAppLink, WHATSAPP_BASE_URL } from '../../utils/whatsapp';

// WhatsApp SVG Icon
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Packs data with translations
const getPacksTranslated = (language, originalPacks) => {
  const isEn = language === 'en';
  return originalPacks.map(pack => ({
    ...pack,
    badge: pack.badge ? (isEn ? '⭐ Most popular' : pack.badge) : null,
    subtitle: isEn ? {
      1: 'The essentials of Hurghada made simple',
      2: 'Turquoise sea & unforgettable moments',
      3: 'Egyptian culture & Red Sea combined'
    }[pack.id] || pack.subtitle : pack.subtitle
  }));
};

const PackagesSection = () => {
  const { language, isEnglish } = useLanguage();
  const { t } = useTranslation();
  const translatedPacks = getPacksTranslated(language, packsData);

  // Generate WhatsApp link for pack booking (NO EMOJIS)
  const getPackWhatsAppLink = (packName) => {
    const message = isEnglish 
      ? `Hello,
I would like to book the package: ${packName}

Preferred date:
Number of people:
Hotel:

Thank you.`
      : `Bonjour,
Je souhaite réserver le pack : ${packName}

Date souhaitée :
Nombre de personnes :
Hôtel :

Merci.`;
    return `${WHATSAPP_BASE_URL}?text=${encodeURIComponent(message)}`;
  };

  // Generate WhatsApp link for custom program (NO EMOJIS)
  const getCustomProgramLink = () => {
    const message = isEnglish 
      ? `Hello,
I would like to create a custom program for my stay in Hurghada.

My dates:
My style (relaxation / adventure / culture):

Thank you.`
      : `Bonjour,
Je souhaite créer un programme sur mesure pour mon séjour à Hurghada.

Mes dates :
Mon style (détente / aventure / culture) :

Merci.`;
    return `${WHATSAPP_BASE_URL}?text=${encodeURIComponent(message)}`;
  };

  return (
    <section id="packages" className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-cyan-50 text-cyan-600 rounded-full text-sm font-medium mb-4 animate-in fade-in slide-in-from-bottom duration-500">
            {t('packages.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 animate-in fade-in slide-in-from-bottom duration-500 delay-100">
            {t('packages.title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-cyan-500">
              {t('packages.titleHighlight')}
            </span>
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom duration-500 delay-200">
            {t('packages.subtitle')}
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-start">
          {translatedPacks.map((pkg, index) => (
            <div
              key={pkg.id}
              className={`relative bg-white rounded-2xl overflow-hidden border-2 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl animate-in fade-in slide-in-from-bottom duration-500 flex flex-col ${
                pkg.popular
                  ? 'border-teal-400 shadow-xl shadow-teal-100/50 md:scale-[1.03]'
                  : 'border-slate-100 hover:border-teal-200'
              }`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Badge - Enhanced visibility for popular pack */}
              {pkg.badge && (
                <div className="absolute top-4 left-4 z-10">
                  <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-full text-sm font-semibold shadow-lg shadow-teal-200/50">
                    <Star className="w-4 h-4" fill="currentColor" />
                    {pkg.badge}
                  </div>
                </div>
              )}

              {/* Image - Fixed height for all cards (30-35% of card) */}
              <div className="relative overflow-hidden h-48 sm:h-56 md:h-60">
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
              </div>

              {/* Package Content - Enhanced spacing */}
              <div className="p-6 md:p-8 flex flex-col flex-grow">
                {/* Title with more breathing room */}
                <div className="text-center mb-5 pt-1">
                  <h3 className={`font-bold text-slate-800 mb-2 ${pkg.popular ? 'text-xl md:text-2xl' : 'text-xl'}`}>
                    {pkg.name}
                  </h3>
                  <p className="text-sm text-slate-500">{pkg.subtitle}</p>
                </div>

                {/* Includes List - Better spacing */}
                <div className="space-y-2.5 mb-8 flex-grow">
                  <p className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">{t('packages.included')}</p>
                  {pkg.activities.map((activity, idx) => (
                    <div key={idx} className="flex items-center gap-3 py-1.5">
                      <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-teal-600" />
                      </div>
                      <span className="text-slate-700">{activity.name}</span>
                    </div>
                  ))}
                </div>

                {/* Price - Enhanced spacing above */}
                <div className="text-center mb-5">
                  <span className={`font-bold text-teal-600 ${pkg.popular ? 'text-3xl md:text-4xl' : 'text-3xl'}`}>{pkg.packPrice} €</span>
                </div>

                {/* Reservation Info Bubble - Contains Stripe CTA INSIDE */}
                <div className="w-full text-center mb-5 p-4 bg-emerald-50 rounded-xl relative z-10">
                  <p className="text-sm font-semibold text-emerald-700 mb-1">
                    {isEnglish ? `Book now for ${pkg.depositPrice} €` : `Réservez maintenant pour ${pkg.depositPrice} €`}
                  </p>
                  <p className="text-xs text-slate-500 mb-4">
                    {isEnglish ? `Balance on site: ${pkg.remainingPrice} €` : `Solde à régler sur place : ${pkg.remainingPrice} €`}
                  </p>
                  
                  {/* Stripe CTA INSIDE the bubble */}
                  <a
                    href={pkg.stripeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="w-full block rounded-full py-3 transition-all duration-300 text-white shadow-lg hover:shadow-xl active:scale-95 font-medium touch-manipulation mb-2 text-center"
                    style={{ 
                      backgroundColor: '#635BFF',
                      WebkitTapHighlightColor: 'rgba(99, 91, 255, 0.3)'
                    }}
                  >
                    {isEnglish ? 'Book this pack' : 'Réserver ce pack'}
                  </a>
                  <p className="text-xs text-slate-500 text-center">
                    🔒 {isEnglish ? 'Secure payment · Deposit only' : 'Paiement sécurisé · Acompte uniquement'}
                  </p>
                </div>

                {/* WhatsApp CTA - BELOW the bubble */}
                <Button
                  asChild
                  className="w-full rounded-full py-5 transition-all duration-300 bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-200 hover:shadow-emerald-300"
                >
                  <a href={getPackWhatsAppLink(pkg.name)} target="_blank" rel="noopener noreferrer">
                    <WhatsAppIcon className="w-4 h-4 mr-2" />
                    {isEnglish ? '💬 Questions? WhatsApp' : '💬 Questions ? WhatsApp'}
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Program CTA Block */}
        <div className="mt-12 text-center bg-white rounded-2xl p-8 md:p-10 border border-slate-100 shadow-sm max-w-2xl mx-auto">
          <p className="text-lg md:text-xl font-bold text-slate-800 mb-2">
            {isEnglish ? '❓ Not sure which pack is best for you?' : '❓ Pas sûr du meilleur pack pour vous ?'}
          </p>
          <p className="text-slate-600 mb-6">
            {isEnglish 
              ? '👉 Tell me your dates + your style (relaxation / adventure / culture) and I\'ll suggest the ideal program on WhatsApp.'
              : '👉 Dites-moi vos dates + votre style (détente / aventure / culture) et je vous propose le programme idéal sur WhatsApp.'}
          </p>
          <Button
            asChild
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all duration-300 whitespace-nowrap"
          >
            <a href={getCustomProgramLink()} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon className="w-5 h-5 mr-2 flex-shrink-0" />
              {isEnglish ? 'Tailor-made program' : 'Programme sur mesure'}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PackagesSection;
